import { Button } from '@base-ui/react/button';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import styled from 'styled-components';
import api from '@/api';
import { Badge } from '~/components/ui/Badge';
import { Field, FieldLabel, FieldValue } from '~/components/ui/FieldDisplay';
import { PageTitle } from '~/components/ui/Page';
import { FieldGrid, Section, SectionTitle } from '~/components/ui/Section';
import { useStudentFocus } from '~/modules/studentFocus';
import buttonStyles from '~/styles/button.module.css';
import { ReassignStudyHelperDialog } from './ReassignStudyHelperDialog';

export const Route = createFileRoute('/_admin/students/$studentId')({
  component: StudentDetail,
});

function StudentDetail() {
  const { studentId } = Route.useParams();
  const [reassignOpen, setReassignOpen] = useState(false);
  const { focusedStudent, enterFocus, exitFocus } = useStudentFocus();
  const { data, isLoading, error } = api.getStudent.useQuery({
    path: { student_id: Number(studentId) },
  });

  const { data: helper } = api.getStudyHelper.useQuery(
    {
      path: { study_helper_id: data?.study_helper_id ?? 0 },
    },
    { enabled: !!data?.study_helper_id },
  );

  if (isLoading) return <Message>Loading student...</Message>;
  if (error) return <ErrorMessage>Error loading student</ErrorMessage>;
  if (!data) return <ErrorMessage>Student not found</ErrorMessage>;

  return (
    <Container>
      <BackLink to="/students">&larr; Back to Students</BackLink>

      <Header>
        <PageTitle>{data.display_name}</PageTitle>
        <HeaderActions>
          {data.suspended ? <Badge $variant="error">Suspended</Badge> : null}
          {focusedStudent?.id === data.id ? (
            <Button className={buttonStyles.Button} onClick={exitFocus}>
              Exit student focus
            </Button>
          ) : (
            <Button
              className={buttonStyles.Button}
              onClick={() => enterFocus(data.id, data.display_name)}
            >
              Focus on this student
            </Button>
          )}
        </HeaderActions>
      </Header>

      <Section>
        <SectionTitle>General Information</SectionTitle>
        <FieldGrid>
          <Field>
            <FieldLabel>ID</FieldLabel>
            <FieldValue>{data.id}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Name</FieldLabel>
            <FieldValue>
              {data.first_name} {data.last_name}
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>Email</FieldLabel>
            <FieldValue>{data.email || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Phone</FieldLabel>
            <FieldValue>{data.phone || '—'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Profile Type</FieldLabel>
            <FieldValue>{data.profile_type}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Gender</FieldLabel>
            <FieldValue>{data.gender}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Language</FieldLabel>
            <FieldValue>{data.language}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Location</FieldLabel>
            <FieldValue>
              {[data.city, data.region, data.country]
                .filter(Boolean)
                .join(', ') || '—'}
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>Study Helper</FieldLabel>
            <FieldValue>
              <StudyHelperRow>
                <StudyHelperText>
                  {data.study_helper_id
                    ? helper
                      ? `${helper.display_name} (#${helper.id})`
                      : `#${data.study_helper_id}`
                    : '— Unassigned'}
                </StudyHelperText>
                <Button
                  className={buttonStyles.Button}
                  onClick={() => setReassignOpen(true)}
                >
                  Reassign
                </Button>
              </StudyHelperRow>
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>Introduced</FieldLabel>
            <FieldValue>{data.needs_introduction ? 'No' : 'Yes'}</FieldValue>
          </Field>
          <Field>
            <FieldLabel>Registered</FieldLabel>
            <FieldValue>
              {data.registered
                ? new Date(data.registered).toLocaleString()
                : '—'}
            </FieldValue>
          </Field>
          <Field>
            <FieldLabel>Last Active</FieldLabel>
            <FieldValue>
              {data.last_active
                ? new Date(data.last_active).toLocaleString()
                : '—'}
            </FieldValue>
          </Field>
        </FieldGrid>
      </Section>

      <Section>
        <SectionTitle>Activity</SectionTitle>
        <ActivityLinks>
          <Link
            to="/quiz-submissions"
            search={{ user_id: data.id }}
            style={{
              color: 'var(--blue)',
              textDecoration: 'none',
            }}
          >
            View quiz submissions &rarr;
          </Link>
          <Link
            to="/certificates"
            search={{ user_id: data.id }}
            style={{
              color: 'var(--blue)',
              textDecoration: 'none',
            }}
          >
            View certificates &rarr;
          </Link>
        </ActivityLinks>
      </Section>

      <ReassignStudyHelperDialog
        open={reassignOpen}
        onOpenChange={setReassignOpen}
        studentId={data.id}
        currentStudyHelperId={data.study_helper_id ?? null}
      />
    </Container>
  );
}

const Container = styled.div``;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--blue);
  text-decoration: none;
  margin-bottom: 1rem;
  font-size: 0.875rem;

  &:hover {
    text-decoration: underline;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ActivityLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.25rem 0;
`;

const StudyHelperRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const StudyHelperText = styled.span`
  flex: 1;
`;

const Message = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--grey-600);
`;

const ErrorMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: var(--error);
`;
